import React, { useMemo } from 'react';

// Confirmed-policy-denial screen (docs/amends/user-access-restriction/tasks.md,
// P5-1). Deliberately distinct from frontend/src/accessPermissions.js, which is
// reserved for object-level permission failures on an already-valid account -
// this screen is for accounts that have no valid access to the application at
// all (see P5-3). Rendered both as a brief holding screen by App.js while the
// denial procedure runs, and as the destination of the public /access-denied
// route (PublicAccessDeniedRoute.js) reached after the resulting MSAL logout.
//
// `correlationId`, when provided, is the backend's own access-control log
// correlation ID (Phase 7 - api/server/graphql/resolvers/Shared/AccessLogger.js),
// genuinely traceable against server-side logs. If it's ever missing (e.g. an
// older client, or a request that failed before one was generated), a
// client-only breadcrumb is generated as a fallback so the screen always shows
// *something* a user can quote to support, even though that fallback has no
// matching backend log line.
//
// The exact support/contact wording has not been confirmed with whoever owns
// the support/help-desk process (flagged as an open item in
// remediation-plan.md §11) - the copy below is generic pending that sign-off.
const generateFallbackReference = () => {
    const timePart = Date.now().toString(36).toUpperCase();
    const randomPart = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `ACC-${timePart}-${randomPart}`;
};

const SystemAccessDenied = ({ reasonCode, correlationId, onSignOut, signOutLabel = 'Sign out' }) => {
    const fallbackReference = useMemo(() => generateFallbackReference(), []);
    const displayedReference = correlationId || fallbackReference;

    return (
        <div className="flex flex-col w-full items-center justify-center" style={{ padding: '3rem 1rem', textAlign: 'center' }}>
            <h2>Access restricted</h2>
            <p>Your signed-in account does not currently have access to this application.</p>
            <p>If you believe this is incorrect, please contact your organisation's IT support or system administrator.</p>
            {reasonCode && <p><small>Reason code: {reasonCode}</small></p>}
            <p><small>Support reference: {displayedReference}</small></p>
            <p>
                <button onClick={onSignOut}>{signOutLabel}</button>
            </p>
        </div>
    );
};

export default SystemAccessDenied;
